import { useEffect, useState } from "react";
import { renderMD } from "../seller/lib/markdown";

const BETA_SECRET = process.env.REACT_APP_BETA_SECRET || "sixzero2026";
const fmtPrice = (n) => n == null ? "—" : `$${Math.round(n).toLocaleString()}`;

const COMPS_SYSTEM = "You are a real estate analyst helping a home BUYER evaluate whether a listing is fairly priced. Use web_search to find recent comparable sales. Numbers over words. No disclaimers. Frame everything from the buyer's perspective — is this a good deal for them, not what the seller should list at.";

const buildCompsPrompt = (address, factsLine, listPrice) => `Property: ${address}
${factsLine}
${listPrice ? `Listed at: $${Math.round(listPrice).toLocaleString()}` : ""}

Web-search recent active and sold comparable sales within 0.5 miles in the last 6 months. Produce a buyer-focused comps analysis.

Output EXACTLY this format:

**Verdict: Fair / Overpriced / Underpriced** — one sentence why.

**Comparable sales:**
- (3-4 bullets, each: address · beds/baths · sqft · sale price · $/sqft · sale date)

**What this means for your offer:**
- (2-3 bullets — negotiating leverage, or reasons to move fast if underpriced)

Total response under 220 words. No preamble.`;

// Full property detail — the "Zillow-like" page. Opened from BuyerApp's
// existing mini .b-detail card via a "Full details" button, as an overlay
// so the map/list layout underneath is untouched.
//
// Disclosures are address-matched against 6zero's own live listings
// (api/listings.js's getDisclosures action, since RLS blocks a buyer from
// reading another user's seller_listings row directly) — real data only
// for 6zero's own listings, an explicit "not available" state for every
// externally-sourced search result. No fabricated disclosure data; MLS
// disclosure feeds don't exist for us to pull from (see project notes).
export default function PropertyDetail({ listing, onClose, onMakeOffer, saved, onToggleSave, auth, agentAccepted, requestAgentGate }) {
  const [attom, setAttom] = useState(null);
  const [attomBusy, setAttomBusy] = useState(false);
  const [valuation, setValuation] = useState(null);
  const [valBusy, setValBusy] = useState(false);
  const [disclosures, setDisclosures] = useState(null); // null = loading
  const [comps, setComps] = useState(null);
  const [compsBusy, setCompsBusy] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [showForm, setShowForm] = useState({ date: "", time: "", name: auth?.profile?.name || "", email: auth?.user?.email || "", phone: "" });
  const [showBusy, setShowBusy] = useState(false);
  const [showErr, setShowErr] = useState("");
  const [showDone, setShowDone] = useState(false);

  const submitShowing = async (e) => {
    e.preventDefault();
    setShowErr("");
    if (!showForm.date || !showForm.time) { setShowErr("Pick a date and time."); return; }
    if (!showForm.name.trim()) { setShowErr("Your name is required."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(showForm.email)) { setShowErr("Enter a valid email."); return; }
    setShowBusy(true);
    try {
      const res = await fetch("/api/listings", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-beta-token": BETA_SECRET },
        body: JSON.stringify({
          action: "schedule_showing",
          buyer_id: auth?.user?.id || null,
          buyer_name: showForm.name,
          buyer_email: showForm.email,
          buyer_phone: showForm.phone,
          listing,
          requested_at: new Date(`${showForm.date}T${showForm.time}`).toISOString(),
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Couldn't schedule showing");
      setShowDone(true);
    } catch (err) {
      setShowErr(err.message);
    }
    setShowBusy(false);
  };

  const fullAddr = [listing.address, listing.city, listing.state].filter(Boolean).join(", ");
  const normAddr = fullAddr.toLowerCase().replace(/\s+/g, " ").replace(/[.,]/g, "").trim();

  const runComps = async () => {
    setCompsBusy(true);
    const factsLine = [
      listing.beds != null && `${listing.beds} beds`,
      listing.baths != null && `${listing.baths} baths`,
      listing.sqft && `${listing.sqft.toLocaleString()} sqft`,
      listing.year_built && `built ${listing.year_built}`,
    ].filter(Boolean).join(" / ");
    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-beta-token": BETA_SECRET },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 2500,
          system: COMPS_SYSTEM,
          messages: [{ role: "user", content: buildCompsPrompt(fullAddr, factsLine, listing.price) }],
          mode: "buyer_comps",
          cache_key: `comps:${normAddr}`,
          cache_type: "valuation",
        }),
      });
      const d = await res.json();
      const text = d.content?.map((b) => b.text || "").join("") || "";
      setComps(text || "Couldn't generate comps right now — try again.");
    } catch {
      setComps("Couldn't generate comps right now — try again.");
    }
    setCompsBusy(false);
  };

  useEffect(() => {
    let cancelled = false;

    // Public-record enrichment (lot size, assessed value) — same endpoint
    // and shape the seller flow already uses.
    setAttomBusy(true);
    fetch("/api/property", {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-beta-token": BETA_SECRET },
      body: JSON.stringify({ address: fullAddr }),
    })
      .then((r) => r.json())
      .then((d) => { if (!cancelled && d?.property?.[0] && !d.property[0]._mocked) setAttom(d.property[0]); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setAttomBusy(false); });

    // AI fair-price estimate — same prompt/cache pattern as BuyerApp's
    // mini-card valuation.
    setValBusy(true);
    const factsLine = [
      listing.beds != null && `${listing.beds} beds`,
      listing.baths != null && `${listing.baths} baths`,
      listing.sqft && `${listing.sqft.toLocaleString()} sqft`,
      listing.year_built && `built ${listing.year_built}`,
    ].filter(Boolean).join(" / ");
    fetch("/api/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-beta-token": BETA_SECRET },
      body: JSON.stringify({
        model: "claude-haiku-4-5-20251001",
        max_tokens: 2000,
        system: "You are a real estate appraiser. Produce a concise fair-price estimate for a home buyer. Use web_search to find recent comparable sales. Numbers only — no disclaimers.",
        messages: [{ role: "user", content: `Property: ${fullAddr}\n${factsLine}\n\nOutput EXACTLY:\n**Fair price range: $X,XXX,XXX – $X,XXX,XXX**\n**Recommended offer: $X,XXX,XXX**\nOne sentence: why.\nUnder 60 words total.` }],
        mode: "buyer_valuation",
        cache_key: `valuation:${normAddr}`,
        cache_type: "valuation",
      }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        const text = d.content?.map((b) => b.text || "").join("") || "";
        const m = text.match(/\$\s*([\d.,]+)\s*([MmKk])?\s*[–\-—to]+\s*\$\s*([\d.,]+)\s*([MmKk])?/);
        if (!m) return;
        const parse = (s, suf) => { let n = parseFloat(s.replace(/,/g, "")); if (/m/i.test(suf || "")) n *= 1e6; if (/k/i.test(suf || "")) n *= 1e3; return n; };
        const low = parse(m[1], m[2]), high = parse(m[3], m[4]);
        if (low && high) setValuation({ low, high, midpoint: Math.round((low + high) / 2) });
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setValBusy(false); });

    // Disclosures — only real for 6zero's own live listings. Exact lookup by
    // seller_listing_id when the search result already carries it (sixZero-
    // exclusive listings); fuzzy address match otherwise (always resolves to
    // "not available" for externally-sourced results, which is correct).
    const disclosuresUrl = listing.seller_listing_id
      ? `/api/listings?disclosures=1&seller_listing_id=${encodeURIComponent(listing.seller_listing_id)}`
      : `/api/listings?disclosures=1&address=${encodeURIComponent(fullAddr)}`;
    fetch(disclosuresUrl, {
      headers: { "x-beta-token": BETA_SECRET },
    })
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setDisclosures(d); })
      .catch(() => { if (!cancelled) setDisclosures({ available: false }); });

    return () => { cancelled = true; };
  }, [fullAddr]); // eslint-disable-line

  const b = attom?.building;
  const lotSqft = attom?.lot?.lotsize2 || attom?.lot?.lotsize1;
  const assessed = attom?.assessment?.assessed?.assdttlvalue;
  const photos = disclosures?.available ? disclosures.photos : [];
  const heroPhoto = photos[0] || listing.photo;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,25,20,0.75)", zIndex: 250, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 720, maxHeight: "92vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ position: "relative" }}>
          {heroPhoto ? (
            <img src={heroPhoto} alt={fullAddr} style={{ width: "100%", height: 280, objectFit: "cover", display: "block", borderRadius: "20px 20px 0 0" }} />
          ) : (
            <div style={{ width: "100%", height: 140, background: "linear-gradient(135deg,#dbe9e2,#b2d4c0)", borderRadius: "20px 20px 0 0" }} />
          )}
          <button onClick={onClose} style={{ position: "absolute", top: 14, right: 14, width: 34, height: 34, borderRadius: "50%", background: "rgba(255,255,255,0.92)", border: "none", cursor: "pointer", fontSize: 18 }}>×</button>
        </div>

        <div style={{ padding: "22px 26px 28px" }}>
          {listing.exclusive && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#e4f7ef", color: "#2d7a62", border: "1px solid #b8e8d4", borderRadius: 8, padding: "3px 10px", fontSize: 11, fontFamily: "'DM Mono',monospace", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>
              ★ sixZero exclusive — no middleman
            </div>
          )}
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, color: "#1c2b25" }}>{fmtPrice(listing.price)}</div>
          <div style={{ fontSize: 14, color: "#5b7365", marginTop: 4 }}>{fullAddr}</div>

          <div style={{ display: "flex", gap: 16, margin: "14px 0", fontSize: 13, fontFamily: "'DM Mono',monospace", color: "#5a8a78" }}>
            <span><strong style={{ color: "#1c2b25" }}>{listing.beds ?? "—"}</strong> bd</span>
            <span><strong style={{ color: "#1c2b25" }}>{listing.baths ?? "—"}</strong> ba</span>
            <span><strong style={{ color: "#1c2b25" }}>{listing.sqft ? listing.sqft.toLocaleString() : "—"}</strong> sqft</span>
            {listing.year_built && <span>built {listing.year_built}</span>}
          </div>

          {(lotSqft || assessed) && (
            <div style={{ display: "flex", gap: 16, marginBottom: 14, fontSize: 12, fontFamily: "'DM Mono',monospace", color: "#9bbfb2" }}>
              {lotSqft && <span>lot {Number(lotSqft).toLocaleString()} sqft</span>}
              {assessed && <span>assessed {fmtPrice(assessed)}</span>}
            </div>
          )}
          {attomBusy && !attom && <div style={{ fontSize: 11, color: "#9bbfb2", marginBottom: 10 }}>Looking up public records…</div>}

          <div style={{ background: "#f0faf5", border: "1px solid #c4e8d8", borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: "#5a8a78", letterSpacing: "0.1em", marginBottom: 4 }}>AI FAIR-PRICE ESTIMATE</div>
            {valBusy && !valuation && <div style={{ fontSize: 12, color: "#9bbfb2" }}>Estimating…</div>}
            {valuation && (
              <>
                <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700, color: "#1c5448" }}>
                  {fmtPrice(valuation.low)} – {fmtPrice(valuation.high)}
                </div>
                {listing.price && (
                  <div style={{ fontSize: 11, color: "#5a8a78", marginTop: 2 }}>
                    Listed at {fmtPrice(listing.price)} —{" "}
                    {listing.price > valuation.high ? "above range" : listing.price < valuation.low ? "below range" : "within range"}
                  </div>
                )}
              </>
            )}
            {!valBusy && !valuation && <div style={{ fontSize: 12, color: "#9bbfb2" }}>Estimate unavailable.</div>}
          </div>

          <div style={{ borderTop: "1px solid #f0f4f2", paddingTop: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: "#5a8a78", letterSpacing: "0.1em", marginBottom: 8, textTransform: "uppercase" }}>Comps analysis</div>
            {!comps && (
              <button
                onClick={() => (agentAccepted ? runComps() : requestAgentGate?.("run comps analysis", runComps))}
                disabled={compsBusy}
                style={{ background: "#f2f8f5", color: "#1c2b25", border: "1px solid #d8ede6", borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: compsBusy ? "default" : "pointer", fontFamily: "'Outfit',sans-serif" }}
              >{compsBusy ? "Analyzing comps…" : agentAccepted ? "Run comps analysis →" : "🔒 Run comps analysis →"}</button>
            )}
            {comps && (
              <div style={{ fontSize: 13, color: "#1c2b25", lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: renderMD(comps) }} />
            )}
          </div>

          <div style={{ borderTop: "1px solid #f0f4f2", paddingTop: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: "#5a8a78", letterSpacing: "0.1em", marginBottom: 8, textTransform: "uppercase" }}>Disclosures</div>
            {disclosures === null && <div style={{ fontSize: 12, color: "#9bbfb2" }}>Checking…</div>}
            {disclosures && !disclosures.available && (
              <div style={{ fontSize: 12, color: "#9bbfb2", lineHeight: 1.6, fontStyle: "italic" }}>
                Disclosures aren't available for this listing — sixZero only has them for listings sold through our platform.
              </div>
            )}
            {disclosures?.available && (
              <div style={{ fontSize: 13, color: "#1c2b25", lineHeight: 1.7, background: "#f7faf8", border: "1px solid #e1ede7", borderRadius: 10, padding: "12px 14px" }}>
                {disclosures.summary}
              </div>
            )}
          </div>

          <div style={{ borderTop: "1px solid #f0f4f2", paddingTop: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: "#5a8a78", letterSpacing: "0.1em", marginBottom: 8, textTransform: "uppercase" }}>Schedule a showing</div>
            {showDone ? (
              <div style={{ fontSize: 13, color: "#2d7a62", background: "#e4f7ef", border: "1px solid #b8e8d4", borderRadius: 10, padding: "12px 14px" }}>
                ✓ Showing confirmed — a calendar invite is on its way to {showForm.email}.
              </div>
            ) : !scheduling ? (
              <button onClick={() => (agentAccepted ? setScheduling(true) : requestAgentGate?.("schedule a showing", () => setScheduling(true)))} style={{ background: "#f2f8f5", color: "#1c2b25", border: "1px solid #d8ede6", borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: "pointer", fontFamily: "'Outfit',sans-serif" }}>
                {agentAccepted ? "📅 Request a showing time →" : "🔒 Request a showing time →"}
              </button>
            ) : (
              <form onSubmit={submitShowing} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <input type="date" required value={showForm.date} onChange={(e) => setShowForm((s) => ({ ...s, date: e.target.value }))}
                    style={{ flex: 1, background: "#f7faf8", border: "1.5px solid #d8ede6", borderRadius: 8, padding: "9px 10px", fontSize: 13, fontFamily: "'Outfit',sans-serif" }} />
                  <input type="time" required value={showForm.time} onChange={(e) => setShowForm((s) => ({ ...s, time: e.target.value }))}
                    style={{ flex: 1, background: "#f7faf8", border: "1.5px solid #d8ede6", borderRadius: 8, padding: "9px 10px", fontSize: 13, fontFamily: "'Outfit',sans-serif" }} />
                </div>
                <input placeholder="Your name" required value={showForm.name} onChange={(e) => setShowForm((s) => ({ ...s, name: e.target.value }))}
                  style={{ background: "#f7faf8", border: "1.5px solid #d8ede6", borderRadius: 8, padding: "9px 10px", fontSize: 13, fontFamily: "'Outfit',sans-serif" }} />
                <input type="email" placeholder="Email" required value={showForm.email} onChange={(e) => setShowForm((s) => ({ ...s, email: e.target.value }))}
                  style={{ background: "#f7faf8", border: "1.5px solid #d8ede6", borderRadius: 8, padding: "9px 10px", fontSize: 13, fontFamily: "'Outfit',sans-serif" }} />
                <input placeholder="Phone (optional)" value={showForm.phone} onChange={(e) => setShowForm((s) => ({ ...s, phone: e.target.value }))}
                  style={{ background: "#f7faf8", border: "1.5px solid #d8ede6", borderRadius: 8, padding: "9px 10px", fontSize: 13, fontFamily: "'Outfit',sans-serif" }} />
                {showErr && <div style={{ color: "#c0354a", fontSize: 12 }}>{showErr}</div>}
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="submit" disabled={showBusy} style={{ flex: 1, background: "#2d7a62", color: "#fff", border: "none", borderRadius: 8, padding: "10px 14px", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "'Outfit',sans-serif" }}>
                    {showBusy ? "Booking…" : "Confirm showing"}
                  </button>
                  <button type="button" onClick={() => setScheduling(false)} style={{ background: "none", border: "1px solid #d8ede6", borderRadius: 8, padding: "10px 14px", fontSize: 13, cursor: "pointer" }}>Cancel</button>
                </div>
              </form>
            )}
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => (agentAccepted ? onMakeOffer(listing) : requestAgentGate?.("make an offer", () => onMakeOffer(listing)))} style={{ flex: 1, background: "#152d21", color: "#7ecfb0", border: "none", borderRadius: 10, padding: "12px 16px", fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit',sans-serif" }}>{agentAccepted ? "Make an offer" : "🔒 Make an offer"}</button>
            <button onClick={() => onToggleSave(listing)} style={{ background: "#f2f8f5", color: "#1c2b25", border: "1px solid #d8ede6", borderRadius: 10, padding: "12px 16px", cursor: "pointer" }}>{saved ? "♥" : "♡"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
